import { HttpAgent } from '@ag-ui/client';
import { type Context, type RunAgentInput } from '@ag-ui/core';
import { z } from 'zod';

import { reportErrorToBugsnag } from '@air/utils-error';

import { type CanvasTokenImageNode } from './store/slices/createCanvasTokenNodeSlice';
import { getBrandKitMentions } from './store/useBrandKitMentionStore';
import { expandBrandKitRefsForAgent, getBrandKitLogoNodesForMessages } from './tokens/tokenSerialization';

/** Existing entries win on id collision; a `media` entry is appended when none exists. */
const mergeLogoNodesIntoMediaContext = (
  context: Context[] | undefined,
  logoNodes: Record<string, CanvasTokenImageNode>,
): Context[] => {
  const safeContext = context ?? [];

  if (Object.keys(logoNodes).length === 0) {
    return safeContext;
  }

  const mediaEntry = safeContext.find((entry) => entry.description === 'media');

  if (!mediaEntry) {
    return [...safeContext, { description: 'media', value: JSON.stringify(logoNodes) }];
  }

  try {
    const existingNodes = z.record(z.string(), z.unknown()).parse(JSON.parse(mediaEntry.value));
    const mergedNodes = { ...logoNodes, ...existingNodes };

    return safeContext.map((entry) =>
      entry === mediaEntry ? { description: 'media', value: JSON.stringify(mergedNodes) } : entry,
    );
  } catch (error) {
    reportErrorToBugsnag({
      context: 'Failed to parse media context while merging brand kit logo nodes',
      error,
    });

    return safeContext.map((entry) =>
      entry === mediaEntry ? { description: 'media', value: JSON.stringify(logoNodes) } : entry,
    );
  }
};

/**
 * Mutates only the request clone — `agent.messages` keep `[ref:…]` chips.
 * Ref expansion and logo-node context share one mentions snapshot at request time.
 */
export class BrandKitExpandingHttpAgent extends HttpAgent {
  protected override requestInit(input: RunAgentInput): RequestInit {
    const mentions = getBrandKitMentions();
    const rawUserContents: string[] = [];

    const messages = input.messages.map((message) => {
      if (message.role !== 'user' || typeof message.content !== 'string') {
        return message;
      }

      rawUserContents.push(message.content);

      return { ...message, content: expandBrandKitRefsForAgent(message.content, mentions) };
    });

    const logoNodes = getBrandKitLogoNodesForMessages(rawUserContents, mentions);

    return super.requestInit({
      ...input,
      context: mergeLogoNodesIntoMediaContext(input.context, logoNodes),
      messages,
    });
  }
}
